import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";
import alamoFormImage from "@/assets/alamo-retractable-awning-patio.jpg.asset.json";
import alamoFaqImage from "@/assets/alamo-black-white-striped-awning.jpg.asset.json";

const collection = getCollection("alamo-series")!;

const content: FlagshipContent = {
  eyebrow: "Signature Collection · Residential · Flagship",
  heroKicker: "Residential · San Antonio · Austin · Hill Country · Central Texas",
  lifestyleIntro:
    "Backyards that finally get used again — shaded patios, cool porches, and pool decks built for the way Texas families actually live outside.",
  lifestyleBody:
    "The Alamo Series is Texas Made Shade's residential & outdoor living division. Awnings, patio covers, sail shades, drop curtains, privacy panels, gazebo enclosures, grill covers, and Solair® retractables — welded, sewn, and installed by our own crew for homeowners in San Antonio, Austin, New Braunfels, Boerne, and the Hill Country.",
  solutionsHeading: "The residential & outdoor living line, all in one place",
  solutions: [
    { title: "Custom Awnings", desc: "Welded aluminum frames · Sunbrella® canvas.", to: "/awnings" },
    { title: "Solair® Retractables", desc: "Dealer-installed motorized shade for patios & decks.", to: "/retractable-awnings" },
    { title: "Patio & Pergola Covers", desc: "Fixed & louvered structures for Texas backyards.", to: "/pergola-covers" },
    { title: "Sail Shades", desc: "Tensioned shade sails engineered for Texas wind loads.", to: "/sail-shades" },
    { title: "Exterior Vertical Shades", desc: "Solair® drop screens — motorized privacy & sun control.", to: "/exterior-vertical-shades" },
    { title: "Outdoor Drop Curtains", desc: "Weatherproof canvas curtains for porches & pergolas.", to: "/curtains" },
    { title: "Gazebo Covers & Privacy Panels", desc: "Pergola and gazebo tops plus privacy panels for outdoor rooms.", to: "/gazebo-privacy-panels" },
    { title: "Grill & BBQ Covers", desc: "Custom covers for grills, smokers, and outdoor kitchens.", to: "/covers-tops" },
    { title: "Gazebo Enclosures", desc: "Roll-down enclosures that turn a gazebo into a year-round room.", to: "/pergola-covers" },
    { title: "Solair® vs. Sunsetter / Eclipse", desc: "Compare retractable brands before you buy.", to: "/solair-vs" },
    { title: "Find Your Shade", desc: "Two-minute quiz to match your patio to the right product.", to: "/find-your-shade" },
    { title: "Why Texas Made Shade", desc: "Our shop, our crew, our warranty — see how we build.", to: "/why-texas-made-shade" },
  ],
  trustBadges: [
    "Welded aluminum · 20+ yr frames",
    "Sunbrella® canvas",
    "Solair® Authorized Dealer",
    "Almost 30 yrs experience",
  ],
  formImageSrc: alamoFormImage.url,
  formImageAlt:
    "Retractable patio awning shading a residential deck gathering — Alamo Series residential shade",
  faqImageSrc: alamoFaqImage.url,
  faqImageAlt:
    "Black and white striped Sunbrella radius awning with scalloped valance built in the Texas Made Shade Seguin shop",
  faqs: [
    {
      q: "What's the difference between the Alamo Series and just ordering an awning?",
      a: "Same crew, same shop — the Alamo Series is how we package our residential work: awnings, retractables, patio covers, sail shades, and drop curtains under one residential division so homeowners get a unified quote, install crew, and warranty.",
    },
    {
      q: "Do you install in San Antonio and Austin?",
      a: "Yes. Alamo Series projects install throughout San Antonio, Austin, New Braunfels, Boerne, Seguin, and the Hill Country. Our shop is in Seguin, right between San Antonio and Austin.",
    },
    {
      q: "Do you sell Solair® retractable awnings?",
      a: "Yes — we're an Authorized Solair® Dealer. Solair® systems are quoted, mounted, and installed by our own welding and canvas crew as part of the Alamo Series residential line.",
    },
    {
      q: "How fast can I get a residential quote?",
      a: "Send measurements and photos through the form below or call (830) 390-9588. You'll get a quick reply from a real person, usually same business day.",
    },
    {
      q: "Can you cover an existing pergola or gazebo?",
      a: "Yes — a lot of Alamo Series work is covers, curtains, and enclosures added to a structure you already own. Send photos and rough measurements and we'll size it up.",
    },
    {
      q: "Do drop curtains actually help with sun and wind?",
      a: "Yes. Sunbrella® drop curtains block direct sun and cut wind on the exposed side of a porch or pergola, and they roll up out of the way when you don't need them.",
    },
    {
      q: "Do you build custom grill and outdoor-kitchen covers?",
      a: "All the time. Send the make/model or measurements of your grill, smoker, or built-in and we'll sew a custom cover in Sunbrella®.",
    },
  ],
};

export const Route = createFileRoute("/alamo-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Alamo Series™ — Residential Shade & Covers | Texas Made Shade",
      description: collection.description,
      keywords:
        "residential awnings san antonio, residential awnings austin, custom patio covers texas, solair retractable awnings texas, hill country patio shade, alamo series, texas made shade residential, pergola covers austin, outdoor drop curtains texas, gazebo privacy panels, custom grill covers",
    }),
    links: leafCanonicalLink(ctx, "/alamo-series"),
    scripts: serviceHeadScripts({
      path: "/alamo-series",
      serviceName: "Alamo Series Residential Shade",
      serviceType: "Residential Shade & Covers",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Alamo Series — Residential",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
